import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatAmount } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  Combobox,
  EmptyState,
  Input,
  PageContainer,
  PageHeader,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { invoicePath } from '../../lib/slug';
import { catalogApi } from '../catalog/catalogApi';
import { purchaseOrdersApi } from '../purchase-orders/purchaseOrdersApi';
import { settingsApi } from '../settings/settingsApi';
import { invoicesApi } from './invoicesApi';
import '../requisitions/requisitions.css';
import './invoices.css';

const todayIso = () => new Date().toISOString().slice(0, 10);
const num = (v) => { const n = Number(v); return Number.isNaN(n) ? 0 : n; };

// Mirror the server-side three-way-match check for a live preview. Percent tolerances come from
// settings; the absolute floors match the server's built-in rounding allowances.
const DEFAULT_TOLERANCES = { priceTolerancePercent: 1, quantityTolerancePercent: 0 };
const priceOut = (invUnit, poUnit, tol) =>
  Math.abs(invUnit - poUnit) > Math.max(Math.abs(poUnit) * (tol.priceTolerancePercent / 100), 0.005);
const qtyOut = (invQty, received, tol) =>
  Math.abs(invQty - received) > Math.max(Math.abs(received) * (tol.quantityTolerancePercent / 100), 0.0001);

export default function InvoiceFormPage() {
  const navigate = useNavigate();
  const t = useT();
  const toast = useToast();
  const { activePropertyId, hasPermission } = useProperty();
  const canManage = hasPermission('invoices.manage');

  const [pos, setPos] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tolerances, setTolerances] = useState(DEFAULT_TOLERANCES);

  const [poId, setPoId] = useState('');
  const [po, setPo] = useState(null);
  const [poLoading, setPoLoading] = useState(false);

  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [tax, setTax] = useState('');
  const [freight, setFreight] = useState('');
  const [other, setOther] = useState('');
  const [lines, setLines] = useState([]); // { poLineId, description, sku, uomId, ordered, received, poUnit, qty, price }

  useEffect(() => {
    let active = true;
    Promise.all([
      purchaseOrdersApi.list({ statuses: 'Sent,PartiallyReceived,Closed', pageSize: 200, sortBy: 'number', descending: true }).catch(() => ({ items: [] })),
      catalogApi.listUnits().catch(() => []),
      settingsApi.getEffectiveInvoiceMatching(activePropertyId).catch(() => null),
    ]).then(([poRes, us, tol]) => {
      if (!active) return;
      setPos(poRes?.items || []);
      setUnits(us || []);
      if (tol) setTolerances(tol);
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [activePropertyId]);

  useEffect(() => {
    if (!poId) { setPo(null); setLines([]); return; }
    let active = true;
    setPoLoading(true);
    purchaseOrdersApi.get(poId)
      .then((full) => {
        if (!active) return;
        setPo(full);
        const seeded = (full.lines || [])
          .filter((l) => Number(l.receivedQuantity) > 0)
          .map((l) => ({
            poLineId: l.lineId,
            description: l.description,
            sku: l.sku,
            uomId: l.uomId,
            ordered: Number(l.quantity),
            received: Number(l.receivedQuantity),
            poUnit: Number(l.unitPrice?.amount || 0),
            qty: String(l.receivedQuantity),
            price: String(l.unitPrice?.amount ?? 0),
          }));
        setLines(seeded);
      })
      .catch(() => { if (active) { setPo(null); setLines([]); } })
      .finally(() => { if (active) setPoLoading(false); });
    return () => { active = false; };
  }, [poId]);

  const poOptions = useMemo(
    () => pos.map((p) => ({ value: p.id, label: `${p.number} · ${p.supplierName || t('No supplier')}` })),
    [pos, t],
  );

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;
  const currency = po?.currency || 'USD';

  const setLine = (poLineId, patch) =>
    setLines((prev) => prev.map((l) => (l.poLineId === poLineId ? { ...l, ...patch } : l)));

  const activeLines = useMemo(() => lines.filter((l) => num(l.qty) > 0), [lines]);

  const subtotal = useMemo(
    () => activeLines.reduce((a, l) => a + num(l.qty) * num(l.price), 0),
    [activeLines],
  );
  const total = subtotal + num(tax) + num(freight) + num(other);
  const fmt = (n) => `${currency} ${formatAmount(n)}`;

  const varianceCount = useMemo(
    () => activeLines.filter((l) => qtyOut(num(l.qty), l.received, tolerances) || priceOut(num(l.price), l.poUnit, tolerances)).length,
    [activeLines, tolerances],
  );

  const submit = useCallback(async () => {
    if (!poId) { toast.error(t('Choose a purchase order first')); return; }
    if (activeLines.length === 0) { toast.error(t('Enter a billed quantity for at least one line')); return; }
    setBusy(true);
    try {
      const created = await invoicesApi.create({
        purchaseOrderId: poId,
        supplierInvoiceNumber: supplierInvoiceNumber.trim() || null,
        invoiceDate: invoiceDate ? new Date(invoiceDate).toISOString() : null,
        dueDate: dueDate ? new Date(dueDate).toISOString() : null,
        notes: notes.trim() || null,
        taxTotal: num(tax),
        freightAmount: num(freight),
        otherCharges: num(other),
        lines: activeLines.map((l) => ({
          purchaseOrderLineId: l.poLineId,
          invoicedQuantity: num(l.qty),
          invoiceUnitPrice: num(l.price),
          notes: null,
        })),
      });
      toast.success(t('Invoice {number} captured', { number: created.number }));
      navigate(invoicePath(created));
    } catch (err) {
      toast.error(t('Could not create invoice'), { description: err?.message });
      setBusy(false);
    }
  }, [poId, supplierInvoiceNumber, invoiceDate, dueDate, notes, tax, freight, other, activeLines, navigate, toast, t]);

  if (!canManage) {
    return (
      <PageContainer size="md">
        <EmptyState icon={<Icon.Lock size={28} />} title={t('Not allowed')} description={t("You don't have permission to capture invoices.")} />
      </PageContainer>
    );
  }

  return (
    <PageContainer size="lg">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/invoices')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Invoices')}
      </Button>

      <PageHeader
        title={t('New invoice')}
        description={t("Pick the purchase order being billed, then enter the quantities and prices the supplier charged. We'll match them against what was ordered and received.")}
        icon={<Icon.Receipt size={22} />}
      />

      {loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : pos.length === 0 ? (
        <Banner tone="warning">{t('No issued purchase orders are available to invoice. A PO must be sent to the supplier (and ideally received) before it can be invoiced.')}</Banner>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <Card>
            <Card.Body>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-3)' }}>
                <div className="ps-field">
                  <span className="ps-field__label">{t('Purchase order')}</span>
                  <Combobox value={poId} onChange={setPoId} options={poOptions} placeholder={t('Select a purchase order…')} searchPlaceholder={t('Search by PO or supplier…')} ariaLabel={t('Purchase order')} />
                </div>
                <Input label={t('Supplier invoice #')} value={supplierInvoiceNumber} onChange={(e) => setSupplierInvoiceNumber(e.target.value)} placeholder={t('Their reference')} />
                <Input label={t('Invoice date')} type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
                <Input label={t('Due date')} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
            </Card.Body>
          </Card>

          {!poId ? (
            <Card><Card.Body><EmptyState icon={<Icon.Receipt size={26} />} title={t('Choose a purchase order')} description={t('Pick the PO this invoice bills against to load its received lines.')} /></Card.Body></Card>
          ) : poLoading ? (
            <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-12)' }}><Spinner size={22} /></div>
          ) : lines.length === 0 ? (
            <Card><Card.Body><EmptyState icon={<Icon.TriangleAlert size={26} />} title={t('Nothing received yet')} description={t('This purchase order has no received lines to invoice. Receive the goods first.')} /></Card.Body></Card>
          ) : (
            <>
              <Card>
                <Card.Header>
                  <Card.Title>{t('Billed lines')}</Card.Title>
                  <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
                    {t('Billed defaults to what was received at the PO price (a clean match). Adjust the quantity or price to what the supplier actually charged — variances are flagged.')}
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="inv-lines">
                    {lines.map((l) => {
                      const invQty = num(l.qty);
                      const invPrice = num(l.price);
                      const pOut = priceOut(invPrice, l.poUnit, tolerances);
                      const qOut = qtyOut(invQty, l.received, tolerances);
                      const uom = uomCode(l.uomId) ? ` ${uomCode(l.uomId)}` : '';
                      const priceDelta = invPrice - l.poUnit;
                      const qtyDelta = invQty - l.received;
                      return (
                        <div className={`inv-line ${(pOut || qOut) ? 'is-variance' : ''}`} key={l.poLineId}>
                          <div className="inv-line__item">
                            <div className="inv-line__name">{l.description}</div>
                            <div className="inv-line__sub">
                              {l.sku ? `${l.sku} · ` : ''}{t('Ordered')} {l.ordered}{uom} · {t('Received')} {l.received}{uom}
                            </div>
                          </div>
                          <div className="inv-line__stat">
                            <span className="inv-line__stat-label">{t('Billed qty')}</span>
                            <Input type="number" min="0" step="any" value={l.qty} onChange={(e) => setLine(l.poLineId, { qty: e.target.value })} />
                            {qOut && <span className={`inv-line__delta ${qtyDelta > 0 ? 'is-up' : 'is-down'}`}>{t('{delta} vs received', { delta: `${qtyDelta > 0 ? '+' : ''}${formatAmount(qtyDelta)}` })}</span>}
                          </div>
                          <div className="inv-line__stat">
                            <span className="inv-line__stat-label">{t('PO price')}</span>
                            <span className="inv-line__stat-value">{currency} {formatAmount(l.poUnit)}</span>
                          </div>
                          <div className="inv-line__stat">
                            <span className="inv-line__stat-label">{t('Billed price')}</span>
                            <Input type="number" min="0" step="any" value={l.price} onChange={(e) => setLine(l.poLineId, { price: e.target.value })} />
                            {pOut && <span className={`inv-line__delta ${priceDelta > 0 ? 'is-up' : 'is-down'}`}>{t('{delta} vs PO', { delta: `${priceDelta > 0 ? '+' : ''}${formatAmount(priceDelta)}` })}</span>}
                          </div>
                          <div className="inv-line__stat" style={{ alignItems: 'flex-end' }}>
                            <span className="inv-line__stat-label">{t('Line total')}</span>
                            <span className="inv-line__stat-value">{fmt(invQty * invPrice)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header><Card.Title>{t('Charges & totals')}</Card.Title></Card.Header>
                <Card.Body>
                  <div className="inv-charges" style={{ marginBottom: 'var(--space-4)' }}>
                    <Input label={t('Tax ({currency})', { currency })} type="number" min="0" step="any" value={tax} onChange={(e) => setTax(e.target.value)} placeholder="0.00" />
                    <Input label={t('Freight ({currency})', { currency })} type="number" min="0" step="any" value={freight} onChange={(e) => setFreight(e.target.value)} placeholder="0.00" />
                    <Input label={t('Other charges ({currency})', { currency })} type="number" min="0" step="any" value={other} onChange={(e) => setOther(e.target.value)} placeholder="0.00" />
                  </div>
                  <Textarea label={t('Notes')} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('Optional')} />
                  <div className="inv-totals" style={{ marginTop: 'var(--space-4)' }}>
                    <div className="inv-totals__row"><span className="pp-cell-muted">{t('Subtotal')}</span><span>{fmt(subtotal)}</span></div>
                    <div className="inv-totals__row"><span className="pp-cell-muted">{t('Tax + freight + other')}</span><span>{fmt(num(tax) + num(freight) + num(other))}</span></div>
                    <div className="inv-totals__row is-grand"><span>{t('Total')}</span><span>{fmt(total)}</span></div>
                  </div>
                </Card.Body>
              </Card>

              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                {varianceCount > 0
                  ? <Badge tone="warning" dot>{varianceCount === 1 ? t('{count} line with variance', { count: varianceCount }) : t('{count} lines with variance', { count: varianceCount })}</Badge>
                  : <Badge tone="success" dot>{t('Clean match')}</Badge>}
                <div style={{ display: 'flex', gap: 'var(--space-2)', marginLeft: 'auto' }}>
                  <Button variant="primary" loading={busy} disabled={activeLines.length === 0} leadingIcon={<Icon.Check size={16} />} onClick={submit}>
                    {t('Capture invoice')}
                  </Button>
                  <Button variant="ghost" onClick={() => navigate('/invoices')} disabled={busy}>{t('Cancel')}</Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </PageContainer>
  );
}
